// =============================================================================
// LAYOUT AND DISPLAY CONFIGURATION TYPES
// =============================================================================
// Reusable configuration types for component layout and display properties

/**
 * Layout configuration - how component is positioned/sized
 * Common to all components
 */
export interface LayoutConfig {
  /**
   * Full-width layout (edge-to-edge)
   */
  fullWidth: boolean;
  
  /**
   * Container width constraint
   */
  container: 'default' | 'narrow' | 'wide' | 'full';
  
  /**
   * Internal padding
   */
  padding: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  
  /**
   * External margin
   */
  margin?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  
  /**
   * Number of columns for grid layouts
   */
  columns?: number;
  
  /**
   * Gap between grid items
   */
  gap?: 'sm' | 'md' | 'lg' | 'xl';
  
  /**
   * Content alignment
   */
  alignment?: 'left' | 'center' | 'right' | 'justify';
  
  /**
   * Vertical alignment
   */
  verticalAlignment?: 'top' | 'center' | 'bottom';
  
  /**
   * Responsive behavior
   */
  responsive?: {
    mobile?: Partial<LayoutConfig>;
    tablet?: Partial<LayoutConfig>;
    desktop?: Partial<LayoutConfig>;
  };
}

/**
 * Display configuration - visual styling
 * Common to all components
 */
export interface DisplayConfig {
  /**
   * Background configuration
   */
  background?: BackgroundConfig;
  
  /**
   * Spacing configuration
   */
  spacing?: SpacingConfig;
  
  /**
   * Border configuration
   */
  border?: BorderConfig;
  
  /**
   * Shadow configuration
   */
  shadow?: ShadowConfig;
  
  /**
   * Minimum height
   */
  minHeight?: string;
  
  /**
   * Maximum height
   */
  maxHeight?: string;
  
  /**
   * Minimum width
   */
  minWidth?: string;
  
  /**
   * Maximum width
   */
  maxWidth?: string;
  
  /**
   * Visibility settings
   */
  visibility?: {
    mobile?: boolean;
    tablet?: boolean;
    desktop?: boolean;
  };
  
  /**
   * Animation settings
   */
  animation?: AnimationConfig;
}

/**
 * Background configuration
 */
export interface BackgroundConfig {
  /**
   * Background color
   */
  color?: string;
  
  /**
   * Background image URL
   */
  image?: string;
  
  /**
   * Background video URL
   */
  video?: string;
  
  /**
   * Background gradient
   */
  gradient?: {
    type: 'linear' | 'radial';
    colors: string[];
    angle?: number;
  };
  
  /**
   * Background size
   */
  size?: 'auto' | 'cover' | 'contain' | string;
  
  /**
   * Background position
   */
  position?: string;
  
  /**
   * Background repeat
   */
  repeat?: 'repeat' | 'no-repeat' | 'repeat-x' | 'repeat-y';
  
  /**
   * Background attachment
   */
  attachment?: 'scroll' | 'fixed' | 'local';
  
  /**
   * Overlay settings
   */
  overlay?: {
    color?: string;
    opacity?: number;
    blend?: 'normal' | 'multiply' | 'screen' | 'overlay' | 'darken' | 'lighten';
  };
}

/**
 * Spacing configuration
 */
export interface SpacingConfig {
  /**
   * Padding (all sides)
   */
  padding?: string;
  
  /**
   * Padding (individual sides)
   */
  paddingTop?: string;
  paddingRight?: string;
  paddingBottom?: string;
  paddingLeft?: string;
  
  /**
   * Margin (all sides)
   */
  margin?: string;
  
  /**
   * Margin (individual sides)
   */
  marginTop?: string;
  marginRight?: string;
  marginBottom?: string;
  marginLeft?: string;
}

/**
 * Border configuration
 */
export interface BorderConfig {
  /**
   * Border width
   */
  width?: string;
  
  /**
   * Border style
   */
  style?: 'solid' | 'dashed' | 'dotted' | 'double' | 'none';
  
  /**
   * Border color
   */
  color?: string;
  
  /**
   * Border radius
   */
  radius?: string;
  
  /**
   * Individual corners
   */
  radiusTopLeft?: string;
  radiusTopRight?: string;
  radiusBottomRight?: string;
  radiusBottomLeft?: string;
}

/**
 * Shadow configuration
 */
export interface ShadowConfig {
  /**
   * Box shadow
   */
  box?: string;
  
  /**
   * Text shadow
   */
  text?: string;
  
  /**
   * Preset shadow sizes
   */
  size?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  
  /**
   * Shadow color
   */
  color?: string;
}

/**
 * Animation configuration
 */
export interface AnimationConfig {
  /**
   * Animation type
   */
  type?: 'fade' | 'slide' | 'scale' | 'rotate' | 'bounce' | 'none';
  
  /**
   * Animation duration (ms)
   */
  duration?: number;
  
  /**
   * Animation delay (ms)
   */
  delay?: number;
  
  /**
   * Animation easing
   */
  easing?: 'linear' | 'ease' | 'ease-in' | 'ease-out' | 'ease-in-out';
  
  /**
   * Animation direction
   */
  direction?: 'normal' | 'reverse' | 'alternate' | 'alternate-reverse';
  
  /**
   * Animation iteration count
   */
  iterations?: number | 'infinite';
}

/**
 * Default layout configuration
 */
export const defaultLayoutConfig: LayoutConfig = {
  fullWidth: false,
  container: 'default',
  padding: 'md',
  columns: 1,
  gap: 'md',
  alignment: 'left',
};

/**
 * Default display configuration
 */
export const defaultDisplayConfig: DisplayConfig = {
  spacing: {
    padding: '1rem',
    margin: '0',
  },
};

/**
 * Helper function to merge layout configs
 */
export function mergeLayoutConfig(
  base: Partial<LayoutConfig>,
  override: Partial<LayoutConfig>
): LayoutConfig {
  return {
    ...defaultLayoutConfig,
    ...base,
    ...override,
    responsive: {
      ...base.responsive,
      ...override.responsive,
    },
  };
}

/**
 * Helper function to merge display configs
 */
export function mergeDisplayConfig(
  base: Partial<DisplayConfig>,
  override: Partial<DisplayConfig>
): DisplayConfig {
  return {
    ...defaultDisplayConfig,
    ...base,
    ...override,
    background: {
      ...base.background,
      ...override.background,
      overlay: {
        ...base.background?.overlay,
        ...override.background?.overlay,
      },
    },
    spacing: {
      ...base.spacing,
      ...override.spacing,
    },
    border: {
      ...base.border,
      ...override.border,
    },
    shadow: {
      ...base.shadow,
      ...override.shadow,
    },
    animation: {
      ...base.animation,
      ...override.animation,
    },
  };
}

/**
 * Convert layout config to CSS classes
 */
export function layoutToClasses(layout: Partial<LayoutConfig>): string {
  const classes: string[] = [];
  
  if (layout.fullWidth) classes.push('layout-full-width');
  if (layout.container) classes.push(`layout-container-${layout.container}`);
  if (layout.padding) classes.push(`layout-padding-${layout.padding}`);
  if (layout.margin) classes.push(`layout-margin-${layout.margin}`);
  if (layout.columns) classes.push(`layout-columns-${layout.columns}`);
  if (layout.gap) classes.push(`layout-gap-${layout.gap}`);
  if (layout.alignment) classes.push(`layout-align-${layout.alignment}`);
  if (layout.verticalAlignment) classes.push(`layout-valign-${layout.verticalAlignment}`);
  
  return classes.join(' ');
}

/**
 * Convert display config to inline styles
 */
export function displayToStyles(display: Partial<DisplayConfig>): React.CSSProperties {
  const styles: React.CSSProperties = {};
  
  // Background
  if (display.background) {
    if (display.background.color) styles.backgroundColor = display.background.color;
    if (display.background.image) styles.backgroundImage = `url(${display.background.image})`;
    if (display.background.size) styles.backgroundSize = display.background.size;
    if (display.background.position) styles.backgroundPosition = display.background.position;
    if (display.background.repeat) styles.backgroundRepeat = display.background.repeat;
    if (display.background.attachment) styles.backgroundAttachment = display.background.attachment;
  }
  
  // Spacing
  if (display.spacing) {
    if (display.spacing.padding) styles.padding = display.spacing.padding;
    if (display.spacing.paddingTop) styles.paddingTop = display.spacing.paddingTop;
    if (display.spacing.paddingRight) styles.paddingRight = display.spacing.paddingRight;
    if (display.spacing.paddingBottom) styles.paddingBottom = display.spacing.paddingBottom;
    if (display.spacing.paddingLeft) styles.paddingLeft = display.spacing.paddingLeft;
    if (display.spacing.margin) styles.margin = display.spacing.margin;
    if (display.spacing.marginTop) styles.marginTop = display.spacing.marginTop;
    if (display.spacing.marginRight) styles.marginRight = display.spacing.marginRight;
    if (display.spacing.marginBottom) styles.marginBottom = display.spacing.marginBottom;
    if (display.spacing.marginLeft) styles.marginLeft = display.spacing.marginLeft;
  }
  
  // Dimensions
  if (display.minHeight) styles.minHeight = display.minHeight;
  if (display.maxHeight) styles.maxHeight = display.maxHeight;
  if (display.minWidth) styles.minWidth = display.minWidth;
  if (display.maxWidth) styles.maxWidth = display.maxWidth;
  
  // Border
  if (display.border) {
    if (display.border.width) styles.borderWidth = display.border.width;
    if (display.border.style) styles.borderStyle = display.border.style;
    if (display.border.color) styles.borderColor = display.border.color;
    if (display.border.radius) styles.borderRadius = display.border.radius;
  }
  
  // Shadow
  if (display.shadow) {
    if (display.shadow.box) styles.boxShadow = display.shadow.box;
    if (display.shadow.text) styles.textShadow = display.shadow.text;
  }
  
  return styles;
}

